/**
 * Jualan (back office): senarai dari pelayan, pratonton, dan cetak semula.
 *
 * Beza dengan senarai di POS: POS hanya melihat jualan pada PC itu (IndexedDB). Back office membaca
 * pelayan, jadi jualan dari kaunter lain boleh dicetak semula — itulah sebab ciri ini ada di sini.
 *
 * Cetakan tetap melalui EJEN pada PC ini: pelayar tidak boleh menyentuh printer. Jika ejen tiada,
 * panel memberi amaran dan tidak berpura-pura berjaya.
 */

import { useCallback, useEffect, useState } from 'react';
import { formatMYR } from '@72on/domain';
import { getSale, listSales, type SaleDetail, type SaleRow } from '../offline/admin.js';
import { printReceipt, receiptJobFromSale } from '../offline/agent.js';
import type { PosStore } from '../offline/store.js';
import { API_BASE } from '../config.js';

interface Props {
  token: string;
  store: PosStore;
  receiptHeader: string;
  receiptFooter: string;
}

function today(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }).format(new Date());
}

function daysAgo(days: number): string {
  const value = new Date();
  value.setDate(value.getDate() - days);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }).format(value);
}

export function SalesPanel({ token, store, receiptHeader, receiptFooter }: Props): React.ReactElement {
  const [from, setFrom] = useState(() => daysAgo(6));
  const [to, setTo] = useState(() => today());
  const [rows, setRows] = useState<SaleRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const [preview, setPreview] = useState<SaleDetail | null>(null);
  const [printing, setPrinting] = useState(false);
  const [agentToken, setAgentToken] = useState('');
  const [agentNeeded, setAgentNeeded] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      setRows(await listSales(API_BASE, token, from, to));
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'gagal memuatkan jualan', error: true });
    } finally {
      setBusy(false);
    }
  }, [token, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openPreview(id: string): Promise<void> {
    setMessage(null);
    try {
      setPreview(await getSale(API_BASE, token, id));
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'gagal memuatkan resit', error: true });
    }
  }

  async function print(): Promise<void> {
    if (preview === null) return;

    const stored = (await store.getAgentToken()) ?? (agentToken.trim() === '' ? undefined : agentToken.trim());
    if (stored === undefined) {
      setAgentNeeded(true);
      setMessage({
        text: 'Ejen cetak diperlukan pada PC ini. Masukkan token ejen di bawah (dari tetingkap ejen).',
        error: true,
      });
      return;
    }

    setPrinting(true);
    setMessage(null);
    try {
      const headerLines = receiptHeader
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== '');

      const job = receiptJobFromSale({
        sale: {
          id: preview.sale.id,
          receipt_no: preview.sale.receipt_no,
          client_created_at: preview.sale.client_created_at,
          subtotal_sen: preview.sale.subtotal_sen,
          discount_sen: preview.sale.discount_sen,
          tax_sen: preview.sale.tax_sen,
          total_sen: preview.sale.total_sen,
          paid_sen: preview.sale.paid_sen,
          change_sen: preview.sale.change_sen,
        },
        lines: preview.lines.map((line) => ({
          description_snapshot: line.description_snapshot,
          qty: line.qty,
          unit: line.unit,
          unit_price_sen: line.unit_price_sen,
          discount_sen: line.discount_sen,
          tax_sen: line.tax_sen,
          line_total_sen: line.line_total_sen,
        })),
        payments: preview.payments,
        // Salinan bukan transaksi tunai: laci tidak dibuka.
        openDrawer: false,
        reprint: true,
        ...(preview.sale.cashier_name === null ? {} : { cashierName: preview.sale.cashier_name }),
        ...(headerLines.length === 0 ? {} : { receiptHeader: headerLines }),
        ...(receiptFooter.trim() === '' ? {} : { receiptFooter: receiptFooter.trim() }),
      });

      const outcome = await printReceipt(stored, job);
      if (outcome.printed || outcome.queued) {
        setMessage({ text: `Resit ${preview.sale.receipt_no} dihantar ke ejen cetak.`, error: false });
      } else {
        setMessage({
          text: `Resit tidak dicetak: ${outcome.error ?? 'semak ejen pada PC ini'}.`,
          error: true,
        });
      }
    } catch (error) {
      setMessage({
        text: `Cetak gagal: ${error instanceof Error ? error.message : 'ralat tidak diketahui'}`,
        error: true,
      });
    } finally {
      setPrinting(false);
    }
  }

  async function saveAgentToken(): Promise<void> {
    const value = agentToken.trim();
    if (value.length < 20) {
      setMessage({ text: 'Token ejen terlalu pendek. Salin semula dari tetingkap ejen.', error: true });
      return;
    }
    await store.saveAgentToken(value);
    setAgentNeeded(false);
    setMessage({ text: 'Token ejen disimpan pada PC ini.', error: false });
  }

  const headerLines = receiptHeader
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');

  return (
    <section className="panel-sales">
      <h2>Jualan</h2>
      <p className="muted">
        Jualan daripada SEMUA kaunter, dibaca dari pelayan — berbeza dengan senarai di POS yang hanya
        melihat jualan pada PC itu.
      </p>

      <div className="printer-row">
        <label>
          Dari
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          Hingga
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <button type="button" className="ghost" disabled={busy} onClick={() => void load()}>
          {busy ? 'Memuatkan…' : 'Muat semula'}
        </button>
      </div>

      {message !== null && <p className={message.error ? 'error' : 'muted'}>{message.text}</p>}

      {agentNeeded && (
        <div className="printer-row">
          <label>
            Token ejen (PC ini)
            <input
              value={agentToken}
              onChange={(event) => setAgentToken(event.target.value)}
              spellCheck={false}
              placeholder="32 aksara"
            />
          </label>
          <button type="button" className="ghost" onClick={() => void saveAgentToken()}>
            Simpan token
          </button>
        </div>
      )}

      {rows !== null && rows.length === 0 && <p className="muted">Tiada jualan dalam tempoh ini.</p>}

      {rows !== null && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Masa</th>
              <th>Resit</th>
              <th>Kasir</th>
              <th>Jumlah</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {new Date(row.created_at).toLocaleString('ms-MY', {
                    timeZone: 'Asia/Kuala_Lumpur',
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </td>
                <td>
                  {row.receipt_no}
                  {row.status !== 'completed' && row.status !== 'synced' ? ` (${row.status})` : ''}
                </td>
                <td>{row.cashier_name ?? '—'}</td>
                <td>RM{formatMYR(row.total_sen)}</td>
                <td>
                  <button type="button" className="ghost" onClick={() => void openPreview(row.id)}>
                    Pratonton
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {preview !== null && (
        <div className="modal">
          <div className="sheet">
            <h3>Pratonton resit {preview.sale.receipt_no}</h3>
            <div className="receipt-preview">
              {headerLines.length === 0 ? (
                <p className="rp-note">Kepala resit diambil daripada tetapan ejen.</p>
              ) : (
                headerLines.map((line) => (
                  <div key={line} className="rp-center rp-bold">
                    {line}
                  </div>
                ))
              )}
              <div className="rp-center rp-bold">RESIT</div>
              <div className="rp-row">
                <span>No</span>
                <span>{preview.sale.receipt_no}</span>
              </div>
              <div className="rp-row">
                <span>Masa</span>
                <span>
                  {new Date(preview.sale.client_created_at).toLocaleString('ms-MY', {
                    timeZone: 'Asia/Kuala_Lumpur',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              {preview.sale.cashier_name !== null && (
                <div className="rp-row">
                  <span>Kasir</span>
                  <span>{preview.sale.cashier_name}</span>
                </div>
              )}
              <div className="rp-sep" />
              {preview.lines.map((line) => (
                <div key={line.id} className="rp-item">
                  <div>{line.description_snapshot}</div>
                  <div className="rp-row">
                    <span>
                      {Number(line.qty)} {line.unit} × RM{formatMYR(line.unit_price_sen)}
                    </span>
                    <span>RM{formatMYR(line.line_total_sen)}</span>
                  </div>
                </div>
              ))}
              <div className="rp-sep" />
              {preview.sale.discount_sen > 0 && (
                <div className="rp-row">
                  <span>Diskaun</span>
                  <span>-RM{formatMYR(preview.sale.discount_sen)}</span>
                </div>
              )}
              {preview.sale.tax_sen > 0 && (
                <div className="rp-row">
                  <span>Cukai</span>
                  <span>RM{formatMYR(preview.sale.tax_sen)}</span>
                </div>
              )}
              <div className="rp-row rp-bold">
                <span>JUMLAH</span>
                <span>RM{formatMYR(preview.sale.total_sen)}</span>
              </div>
              {preview.payments.map((payment) => (
                <div key={payment.id} className="rp-row">
                  <span>{payment.method === 'cash' ? 'Tunai' : payment.method}</span>
                  <span>RM{formatMYR(payment.amount_sen)}</span>
                </div>
              ))}
              <div className="rp-row">
                <span>Baki</span>
                <span>RM{formatMYR(preview.sale.change_sen)}</span>
              </div>
              {receiptFooter.trim() !== '' && (
                <>
                  <div className="rp-sep" />
                  <div className="rp-center">{receiptFooter.trim()}</div>
                </>
              )}
            </div>
            <p className="muted">
              Cetakan berlaku pada ejen PC ini. Laci tidak dibuka kerana salinan bukan transaksi tunai.
            </p>
            <div className="actions">
              <button type="button" className="ghost" onClick={() => setPreview(null)}>
                Kembali
              </button>
              <button type="button" disabled={printing} onClick={() => void print()}>
                {printing ? 'Mencetak…' : 'Cetak resit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
